<script setup>
// Um passo do caminho da ligação (árvore desenhada por destinos.js#caminho):
// o destino e, abaixo, cada ramo com o rótulo ("Aberto", "Tecla 1",
// "Ninguém atendeu"). Recursivo.
import { ICONE } from './destinos';

defineProps({
  no: { type: Object, required: true },
});
// clicar num passo abre a edição dele (fila, URA, horário)
const emit = defineEmits(['editar']);
const EDITAVEL = ['fila', 'ura', 'horario'];
</script>

<template>
  <div class="tel-no" :class="`tel-no--${no.tipo}`">
    <component
      :is="EDITAVEL.includes(no.tipo) ? 'button' : 'span'"
      :type="EDITAVEL.includes(no.tipo) ? 'button' : undefined"
      class="tel-no__cartao"
      :class="{ 'tel-no__cartao--editavel': EDITAVEL.includes(no.tipo) }"
      @click="EDITAVEL.includes(no.tipo) && emit('editar', no.destino)"
    >
      <i :class="ICONE[no.tipo]" class="tel-no__ico" aria-hidden="true" />
      <span class="tel-no__rotulo">{{ no.rotulo }}</span>
      <span v-if="no.membros?.length" class="tel-no__membros">
        <span v-for="m in no.membros" :key="m" class="tel-no__membro">{{
          m
        }}</span>
      </span>
    </component>
    <ul v-if="no.ramos.length" class="tel-no__ramos">
      <li v-for="(r, i) in no.ramos" :key="i" class="tel-no__ramo">
        <span class="tel-no__por">{{ r.rotulo }}</span>
        <CaminhoNo :no="r.no" @editar="d => emit('editar', d)" />
      </li>
    </ul>
  </div>
</template>

<style scoped>
.tel-no {
  --cor: var(--slate-9);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--esp-2);
  min-width: 0;
}
.tel-no--ramal {
  --cor: var(--teal-9);
}
.tel-no--fila {
  --cor: var(--blue-9, var(--teal-9));
}
.tel-no--ura {
  --cor: var(--iris-9, var(--teal-9));
}
.tel-no--horario {
  --cor: var(--amber-9);
}
.tel-no--sumiu {
  --cor: var(--ruby-9);
}
.tel-no__cartao {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
  max-width: 100%;
  padding: 6px var(--esp-3) 6px 6px;
  border-radius: 999px;
  background: rgb(var(--cor) / 10%);
  box-shadow: inset 0 0 0 1px rgb(var(--cor) / 28%);
  animation: tel-no-entra var(--mov-2) var(--mov-curva) both;
}
.tel-no__cartao--editavel {
  cursor: pointer;
  transition:
    box-shadow var(--mov-1) var(--mov-curva),
    transform var(--mov-1) var(--mov-curva);
}
.tel-no__cartao--editavel:hover {
  box-shadow:
    inset 0 0 0 1px rgb(var(--cor) / 60%),
    0 4px 12px rgb(var(--cor) / 18%);
  transform: translateY(-1px);
}
.tel-no__cartao--editavel:focus-visible {
  outline: 2px solid rgb(var(--cor));
  outline-offset: 2px;
}
.tel-no__ico {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 24px;
  height: 24px;
  padding: 5px;
  border-radius: 999px;
  font-size: 14px;
  background: rgb(var(--cor));
  color: white;
  box-sizing: border-box;
}
.tel-no__rotulo {
  min-width: 0;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
}
.tel-no__membros {
  display: inline-flex;
  gap: 3px;
}
.tel-no__membro {
  padding: 1px 6px;
  border-radius: 999px;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
}
.tel-no__ramos {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0 0 0 11px;
  padding: 0 0 0 var(--esp-4);
  border-left: 2px solid rgb(var(--cor) / 30%);
  list-style: none;
}
.tel-no__ramo {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
/* o "galho" que liga a linha ao ramo */
.tel-no__ramo::before {
  position: absolute;
  top: 9px;
  left: calc(-1 * var(--esp-4) - 2px);
  width: calc(var(--esp-4) - 4px);
  height: 2px;
  background: rgb(var(--cor) / 30%);
  content: '';
}
.tel-no__por {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--slate-10));
}
@keyframes tel-no-entra {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .tel-no__cartao {
    animation: none;
  }
}
</style>
